import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { EnvName } from "./api";
import { showApiError } from "./api";
import { useApi } from "./useApi";
import styles from "./external-feeds.module.css";

function feedLabel(source: string): string {
  switch (source) {
    case "notam":
      return "NOTAM";
    case "atis":
      return "ATIS";
    case "weather":
      return "Weather sensor";
    case "equipment":
      return "Equipment";
    default:
      return source.toUpperCase();
  }
}

function formatRecordedAt(iso: string): string {
  return new Date(iso).toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function NotamFeedBody({
  env,
  payload,
  summary,
}: {
  env: EnvName;
  payload: Record<string, unknown>;
  summary: string;
}) {
  const api = useApi(env);
  const qc = useQueryClient();
  const text = typeof payload.text === "string" ? payload.text.trim() : "";
  const isSim = env === "sim";

  const fetchNotam = useMutation({
    mutationFn: () => api.syncNotam("KRCA", true),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ingest-feeds", env] });
    },
  });

  const fetchError = showApiError(fetchNotam.error);

  return (
    <>
      <p className={styles.feedSummary}>{summary}</p>
      {text ? (
        <details className={styles.notamTextFold}>
          <summary>Full NOTAM text</summary>
          <pre className={styles.notamText}>{text}</pre>
        </details>
      ) : isSim ? (
        <p className={`muted ${styles.notamNote}`}>
          Scenario NOTAM from replay YAML. Sim does not call FAA.
        </p>
      ) : (
        <div className={styles.notamMissing}>
          <p className="muted">Full NOTAM text was not saved for this ingest.</p>
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            disabled={fetchNotam.isPending}
            onClick={() => fetchNotam.mutate()}
          >
            {fetchNotam.isPending ? "Fetching…" : "Fetch NOTAM from FAA"}
          </button>
          {fetchError ? <p className="error">{fetchError}</p> : null}
        </div>
      )}
    </>
  );
}

export function ExternalFeedsPanel({
  env,
  compact,
}: {
  env: EnvName;
  compact?: boolean;
}) {
  const api = useApi(env);
  const isSim = env === "sim";
  const { data, isLoading, error } = useQuery({
    queryKey: ["ingest-feeds", env],
    queryFn: api.ingestFeeds,
    refetchOnWindowFocus: true,
  });

  if (isLoading) {
    return (
      <section className={styles.panel} aria-label="External feeds">
        <p className="muted">Loading external feeds…</p>
      </section>
    );
  }
  if (error) {
    return (
      <section className={styles.panel} aria-label="External feeds">
        <p className="error">{(error as Error).message}</p>
      </section>
    );
  }

  const feeds = data?.feeds ?? [];

  return (
    <section
      className={compact ? `${styles.panel} ${styles.panelCompact}` : styles.panel}
      aria-label="External feeds"
    >
      <div className={styles.header}>
        <h2 className={styles.title}>External feeds</h2>
        <p className={`muted ${styles.hint}`}>
          {isSim
            ? "Scenario-injected feeds from replay. Sim never calls FAA or live weather APIs."
            : "NOTAMs refresh from FAA on demand. Conflict finder uses the latest saved feed."}
        </p>
      </div>
      {feeds.length === 0 ? (
        <p className={`muted ${styles.empty}`}>
          {isSim ? "No feeds yet — run Play replay or ingest via scenario steps." : "No feeds ingested yet."}
        </p>
      ) : (
        <ul className={styles.feedList}>
          {feeds.map((feed) => (
            <li
              key={feed.source}
              className={feed.source === "notam" ? `${styles.feedItem} ${styles.feedItemNotam}` : styles.feedItem}
            >
              <div className={styles.feedHead}>
                <span className={styles.feedLabel}>{feedLabel(feed.source)}</span>
                <span className={styles.feedTime}>{formatRecordedAt(feed.recorded_at)}</span>
              </div>
              {feed.source === "notam" ? (
                <NotamFeedBody env={env} payload={feed.payload} summary={feed.summary} />
              ) : (
                <p className={styles.feedSummary}>{feed.summary}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
